import type { CancelHandle, UploadParams } from '../type';
import {
  assertFile,
  createProgressSnapshot,
  getUploadConcurrency,
  getUploadPartSize,
  resolveUploadFileName,
  shouldUseMultipartUpload,
} from '../utils/utils';

interface QiniuUploadProgress {
  total?: {
    loaded?: number;
    total?: number;
  };
}

interface QiniuUploadObserver {
  next?: (progress: QiniuUploadProgress) => void;
  error?: (error: unknown) => void;
  complete?: (result: unknown) => void;
}

interface QiniuUploadSubscription {
  unsubscribe: () => void;
}

interface QiniuUploadObservable {
  subscribe(observer: QiniuUploadObserver): QiniuUploadSubscription;
}

interface QiniuSdkLike {
  upload(
    file: File,
    key: string | null,
    token: string,
    putExtra?: Record<string, unknown>,
    config?: Record<string, unknown>
  ): QiniuUploadObservable;
}

export function upload(
  instance: unknown,
  params: UploadParams,
  uploadToken?: string,
  sdkOtherParams: Record<string, unknown> = {}
): Promise<unknown> {
  const qiniu = instance as QiniuSdkLike;
  const file = assertFile(params.file);
  const fileName = resolveUploadFileName(params);
  const token = params.uploadToken || uploadToken;

  if (!token) {
    throw new Error('ObjectStorage: uploadToken is required for qiniu upload');
  }

  const isMultipart = shouldUseMultipartUpload(file, params.maxSize);
  const putExtra = {
    fname: file.name,
    mimeType: params.contentType || file.type || undefined,
    ...(params.sdkUploadParams ?? {}),
  };
  const uploadConfig = {
    ...sdkOtherParams,
    ...(params.maxSize ? { forceDirect: !isMultipart } : {}),
    chunkSize: bytesToMb(getUploadPartSize(params.partSize)),
    concurrentRequestLimit: getUploadConcurrency(params.queueLength),
  };

  return new Promise((resolve, reject) => {
    let subscription: QiniuUploadSubscription | null = null;
    const cancelHandle: CancelHandle = {
      instance: null,
      cancel() {
        subscription?.unsubscribe();
      },
    };

    subscription = qiniu
      .upload(file, fileName, token, putExtra, uploadConfig)
      .subscribe({
        next(progress) {
          const total = progress.total?.total ?? file.size;
          const loaded = progress.total?.loaded ?? 0;
          params.onUploadProgress?.(createProgressSnapshot(loaded, total));
        },
        error(error) {
          reject(error);
        },
        complete(result) {
          resolve(result);
        },
      });

    params.onCancel?.(cancelHandle);
  });
}

function bytesToMb(bytes: number): number {
  return Math.max(1, Math.ceil(bytes / 1024 / 1024));
}
